import type {ArrWaveformOptions} from './arrWaveform';
export const DEFAULT_HEIGHT = 128;
export function clampToUnit(value: number): number {
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}
export function getRelativePointerPosition(
  rect: DOMRect,
  clientX: number,
  clientY: number
): [number, number] {
  const x = clientX - rect.left;
  const y = clientY - rect.top;
  const relativeX = x / rect.width;
  const relativeY = y / rect.height;
  return [relativeX, relativeY];
}
export function resolveChannelHeight({
  optionsHeight,
  optionsSplitChannels,
  parentHeight,
  numberOfChannels,
  defaultHeight = DEFAULT_HEIGHT,
}: {
  optionsHeight?: ArrWaveformOptions['height'];
  optionsSplitChannels?: ArrWaveformOptions['splitChannels'];
  parentHeight: number;
  numberOfChannels: number;
  defaultHeight?: number;
}): number {
  if (optionsHeight == null) return defaultHeight;
  const numericHeight = Number(optionsHeight);
  if (!isNaN(numericHeight)) return numericHeight;
  if (optionsHeight === 'auto') {
    const height = parentHeight || defaultHeight;
    if (optionsSplitChannels?.every((channel) => !channel.overlay)) {
      return height / numberOfChannels;
    }
    return height;
  }
  return defaultHeight;
}
export function getPixelRatio(devicePixelRatio?: number): number {
  return Math.max(1, devicePixelRatio || 1);
}
export function resolveColorValue(
  color: ArrWaveformOptions['waveColor'],
  devicePixelRatio: number
): string | CanvasGradient {
  if (!Array.isArray(color)) return color || '';
  if (color.length === 0) return '#999';
  if (color.length < 2) return color[0] || '';
  const canvasElement = document.createElement('canvas');
  const ctx = canvasElement.getContext('2d') as CanvasRenderingContext2D;
  const gradientHeight = canvasElement.height * devicePixelRatio;
  const gradient = ctx.createLinearGradient(
    0,
    0,
    0,
    gradientHeight || devicePixelRatio
  );
  const colorStopPercentage = 1 / (color.length - 1);
  color.forEach((value, index) => {
    gradient.addColorStop(index * colorStopPercentage, value);
  });
  return gradient;
}
export function calculateWaveformLayout({
  duration,
  minPxPerSec = 0,
  parentWidth,
  fillParent,
  pixelRatio,
}: {
  duration: number;
  minPxPerSec?: number;
  parentWidth: number;
  fillParent?: boolean;
  pixelRatio: number;
}) {
  const scrollWidth = Math.ceil(duration * minPxPerSec);
  const isScrollable = scrollWidth > parentWidth;
  const useParentWidth = Boolean(fillParent && !isScrollable);
  const width = (useParentWidth ? parentWidth : scrollWidth) * pixelRatio;
  return {
    scrollWidth,
    isScrollable,
    useParentWidth,
    width,
  };
}
export function calculateScrollPercentages({
  scrollLeft,
  clientWidth,
  scrollWidth,
}: {
  scrollLeft: number;
  clientWidth: number;
  scrollWidth: number;
}): {startX: number; endX: number} {
  if (scrollWidth === 0) {
    return {startX: 0, endX: 1};
  }
  const startX = scrollLeft / scrollWidth;
  const endX = (scrollLeft + clientWidth) / scrollWidth;
  return {
    startX: Math.max(0, Math.min(1, startX)),
    endX: Math.max(0, Math.min(1, endX)),
  };
}
export function roundToHalfAwayFromZero(value: number): number {
  const scaled = value * 2;
  const rounded = scaled < 0 ? Math.floor(scaled) : Math.ceil(scaled);
  return rounded / 2;
}
